import { useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { ArrowLeft, AlertTriangle, UserX } from "lucide-react";
import { getClientById, updateClientById, getUsers } from "../config/url";
import { getConfigWithToken } from "../config/config";
import useFetchData from "../hooks/useFetchData";
import ClientFormDrawer from "../components/ClientFormDrawer";
import MemberCardSection from "../components/MemberCardSection";
import classes from "./ClientDetails.module.css";

const BRAND = "#064260";

const formatDateUS = (value) => {
  if (!value) return "—";
  const raw = String(value);
  const d = new Date(raw.includes("T") ? raw : `${raw}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const isActiveStatus = (status) => {
  if (status == null || status === "") return false;
  if (typeof status === "boolean") return status;
  if (typeof status === "number") return status === 1;
  const normalized = String(status).trim().toLowerCase();
  return ["active", "true", "1"].includes(normalized);
};

const ClientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state?.user);
  const token = currentUser?.user?.token || localStorage.getItem("user_id");
  const organizationId =
    currentUser?.user?.organizationId ||
    localStorage.getItem("organizationId") ||
    currentUser?.user?.userId ||
    "";

  const [loading, setLoading] = useState(true);
  const [statusSaving, setStatusSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [client, setClient] = useState(null);

  const requestConfig = useMemo(
    () => ({
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(token ? getConfigWithToken(token).headers : {}),
        ...(organizationId ? { "x-organization-id": organizationId } : {}),
      },
    }),
    [organizationId, token]
  );

  const { data: staffUsers } = useFetchData(getUsers, requestConfig);

  const providers = useMemo(() => {
    const rows = Array.isArray(staffUsers) ? staffUsers : [];
    return rows
      .filter((u) => u && u._id && u.isactive !== false && !u.isDeleted)
      .map((u) => ({
        id: u._id,
        name:
          [u.firstname, u.lastname].filter(Boolean).join(" ").trim() ||
          u.userId ||
          u.email ||
          "Provider",
      }));
  }, [staffUsers]);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(getClientById(id), requestConfig);
      setClient(res.data?.data || res.data);
    } catch (err) {
      if (!silent) setClient(null);
      toast.error(err?.response?.data?.message || "Failed to load client.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const applyStatusChange = async (nextActive) => {
    if (!client) return;
    const previousStatus = client.status;
    const nextStatus = nextActive ? "active" : "inactive";

    setClient((prev) => (prev ? { ...prev, status: nextStatus } : prev));
    setStatusSaving(true);

    try {
      const res = await axios.put(
        updateClientById(id),
        { status: nextStatus },
        requestConfig
      );
      if (res.data?.success !== false && res.data?.success !== "false") {
        toast.success(nextActive ? "Client reactivated." : "Client deactivated.");
        setShowDeactivateModal(false);
        const updated = res.data?.data;
        if (updated) {
          setClient((prev) => ({
            ...(prev || {}),
            ...updated,
            status: updated.status ?? nextStatus,
          }));
        }
        await load({ silent: true });
      } else {
        setClient((prev) => (prev ? { ...prev, status: previousStatus } : prev));
        toast.error(res.data?.message || "Failed to update status.");
      }
    } catch (err) {
      setClient((prev) => (prev ? { ...prev, status: previousStatus } : prev));
      toast.error(err?.response?.data?.message || "Failed to update status.");
    } finally {
      setStatusSaving(false);
    }
  };

  const handleStatusButtonClick = () => {
    if (!client) return;
    if (isActiveStatus(client.status)) {
      setShowDeactivateModal(true);
      return;
    }
    applyStatusChange(true);
  };

  const handleMemberCardUpdate = async (payload) => {
    if (payload?.reload) {
      await load({ silent: true });
      return;
    }
    setClient((prev) => ({ ...(prev || {}), ...payload }));
  };

  const fullName =
    [client?.firstName, client?.lastName].filter(Boolean).join(" ") || "Client";
  const active = isActiveStatus(client?.status);
  const assignedProviderName =
    providers.find((p) => p.id === client?.user_id)?.name ||
    client?.assignedUser ||
    "—";

  if (loading) {
    return (
      <div className={classes.page}>
        <div className={classes.stateCard}>
          <Spinner animation="border" style={{ color: BRAND }} />
          <div className={classes.stateText}>Loading client...</div>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className={classes.page}>
        <div className={classes.stateCard}>
          <p className={classes.stateTitle}>No client found.</p>
          <button
            className={classes.outlineButton}
            onClick={() => navigate("/clients")}
            type="button"
          >
            Back to Clients
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.page}>
      <div className={classes.pageHeader}>
        <button
          aria-label="Back to clients"
          className={classes.backButton}
          onClick={() => navigate("/clients")}
          type="button"
        >
          <ArrowLeft color="#ffffff" size={18} strokeWidth={2.5} />
        </button>
        <h1 className={classes.pageTitle}>Client Profile</h1>
      </div>

      <section className={classes.profileCard}>
        <div className={classes.profileTop}>
          <div className={classes.profileIdentity}>
            <div className={classes.nameRow}>
              <h2 className={classes.clientName}>{fullName}</h2>
              <span
                className={`${classes.statusBadge} ${
                  active ? classes.statusActive : classes.statusInactive
                }`}
              >
                {active ? "Active" : "Inactive"}
              </span>
            </div>
            <p className={classes.clientIdLine}>Client ID: {client.clientid || "—"}</p>
          </div>

          <button
            className={active ? classes.deactivateButton : classes.reactivateButton}
            disabled={statusSaving}
            onClick={handleStatusButtonClick}
            type="button"
          >
            {active ? (
              <>
                <UserX size={16} strokeWidth={2} />
                Deactivate
              </>
            ) : (
              "Activate"
            )}
          </button>
        </div>

        <div className={classes.tabs} role="tablist">
          {[
            { key: "profile", label: "Profile" },
            { key: "visits", label: "Visits" },
            { key: "forms", label: "Encounter Forms" },
            { key: "memberCard", label: "Member Card" },
          ].map((tab) => (
            <button
              aria-selected={activeTab === tab.key}
              className={`${classes.tab} ${
                activeTab === tab.key ? classes.tabActive : ""
              }`}
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              role="tab"
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      {activeTab === "profile" ? (
        <section className={classes.infoSection}>
          <div className={classes.infoHeader}>
            <h3 className={classes.infoTitle}>Client Info</h3>
            <button
              className={classes.editButton}
              onClick={() => setEditDrawerOpen(true)}
              type="button"
            >
              Edit
            </button>
          </div>

          <div className={classes.infoBody}>
            <div className={classes.infoGrid}>
              <div className={classes.infoCell}>
                <span className={classes.fieldLabel}>First Name</span>
                <span className={classes.fieldValue}>{client.firstName || "—"}</span>
              </div>
              <div className={classes.infoCell}>
                <span className={classes.fieldLabel}>Last Name</span>
                <span className={classes.fieldValue}>{client.lastName || "—"}</span>
              </div>
              <div className={classes.infoCell}>
                <span className={classes.fieldLabel}>Date of Birth</span>
                <span className={classes.fieldValue}>{formatDateUS(client.dob)}</span>
              </div>
              <div className={classes.infoCell}>
                <span className={classes.fieldLabel}>Phone Number</span>
                <span className={classes.fieldValue}>{client.phone_number || "—"}</span>
              </div>
              <div className={classes.infoCell}>
                <span className={classes.fieldLabel}>Assigned Provider</span>
                <span className={classes.fieldValue}>{assignedProviderName}</span>
              </div>
              <div className={classes.infoCell}>
                <span className={classes.fieldLabel}>Member ID</span>
                <span className={classes.fieldValue}>
                  {client.payer_member_id || "—"}
                </span>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {activeTab === "memberCard" ? (
        <MemberCardSection
          client={client}
          clientActive={active}
          clientName={fullName}
          onClientUpdate={handleMemberCardUpdate}
          requestConfig={requestConfig}
        />
      ) : null}

      {activeTab === "visits" || activeTab === "forms" ? (
        <section className={classes.placeholderCard}>
          <p className={classes.placeholderTitle}>
            {activeTab === "visits" ? "Visits" : "Encounter Forms"}
          </p>
          <p className={classes.placeholderText}>
            {activeTab === "visits"
              ? "Community visits for this client will appear here."
              : "Encounter forms for this client will appear here."}
          </p>
        </section>
      ) : null}

      <ClientFormDrawer
        client={client}
        mode="edit"
        onClose={() => setEditDrawerOpen(false)}
        onSaved={() => load()}
        open={editDrawerOpen}
      />

      {showDeactivateModal ? (
        <div className={classes.modalRoot} role="presentation">
          <button
            aria-label="Close deactivate confirmation"
            className={classes.modalOverlay}
            onClick={() => !statusSaving && setShowDeactivateModal(false)}
            type="button"
          />
          <div
            aria-labelledby="deactivate-client-title"
            aria-modal="true"
            className={classes.modalCard}
            role="alertdialog"
          >
            <div className={classes.modalIconWrap}>
              <AlertTriangle color="#d32f2f" size={28} strokeWidth={2} />
            </div>
            <h3 className={classes.modalTitle} id="deactivate-client-title">
              Deactivate Client
            </h3>
            <p className={classes.modalText}>
              Are you sure you want to deactivate <strong>{fullName}</strong>? This
              client will no longer appear in active provider caseloads. Historical
              records will remain available.
            </p>
            <div className={classes.modalActions}>
              <button
                className={classes.modalCancel}
                disabled={statusSaving}
                onClick={() => setShowDeactivateModal(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                className={classes.modalDeactivate}
                disabled={statusSaving}
                onClick={() => applyStatusChange(false)}
                type="button"
              >
                <UserX size={16} strokeWidth={2} />
                {statusSaving ? "Deactivating..." : "Deactivate"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ClientDetails;
